/**
 * 主题管理工具模块
 * 提供主题切换、持久化和系统偏好监听功能
 */

const THEME_STORAGE_KEY = 'app_theme_preference'
const DARK_THEME = 'dark'
const LIGHT_THEME = 'light'
const SYSTEM_THEME = 'system'

/**
 * 获取当前主题
 * @returns {string} 'light' | 'dark' | 'system'
 */
export function getTheme () {
  return localStorage.getItem(THEME_STORAGE_KEY) || SYSTEM_THEME
}

/**
 * 设置主题
 * @param {string} theme - 'light' | 'dark' | 'system'
 */
export function setTheme (theme) {
  if (![LIGHT_THEME, DARK_THEME, SYSTEM_THEME].includes(theme)) {
    console.warn(`Invalid theme: ${theme}. Use 'light', 'dark', or 'system'.`)
    return
  }

  localStorage.setItem(THEME_STORAGE_KEY, theme)
  applyTheme(theme)
}

/**
 * 切换主题（浅色 <-> 深色）
 */
export function toggleTheme () {
  const currentTheme = getTheme()
  setTheme(currentTheme === DARK_THEME ? LIGHT_THEME : DARK_THEME)
  // // 如果当前是 system，根据实际应用的模式切换
  // if (currentTheme === SYSTEM_THEME || currentTheme === LIGHT_THEME) {
  //   // 使用 isDarkMode() 获取当前实际状态，更可靠
  //   const isDarkNow = isDarkMode()
  //   setTheme(isDarkNow ? LIGHT_THEME : DARK_THEME)
  // } else {
  //   setTheme(currentTheme === DARK_THEME ? LIGHT_THEME : DARK_THEME)
  // }
}

/**
 * 应用主题到 DOM
 * @param {string} theme - 'light' | 'dark' | 'system'
 */
function applyTheme (theme) {
  if (theme === SYSTEM_THEME) {
    // 跟随系统
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
    setThemeAttribute(prefersDark ? DARK_THEME : LIGHT_THEME)
  } else {
    setThemeAttribute(theme)
  }
}

/**
 * 在 HTML 元素上设置 data-theme 属性
 * @param {string} theme - 'light' | 'dark'
 */
function setThemeAttribute (theme) {
  if (theme === DARK_THEME) {
    document.documentElement.setAttribute('data-theme', DARK_THEME)
  } else {
    document.documentElement.removeAttribute('data-theme')
  }
}

/**
 * 初始化主题
 * 在应用启动时调用，读取用户偏好并应用主题
 */
export function initTheme () {
  const savedTheme = getTheme()
  applyTheme(savedTheme)
  // 监听系统主题变化（仅当用户选择 system 时响应）
  const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
  // 移除可能存在的旧监听器（避免重复）
  try {
    mediaQuery.removeEventListener('change', handleSystemThemeChange)
  } catch (e) {
    // ignore
  }
  // 添加监听器
  mediaQuery.addEventListener('change', handleSystemThemeChange)
}

/**
 * 处理系统主题变化
 * @param {MediaQueryListEvent} event
 */
function handleSystemThemeChange (event) {
  const currentPreference = getTheme()
  // 仅当用户选择了 system 时才响应系统变化
  if (currentPreference === SYSTEM_THEME) {
    applyTheme(SYSTEM_THEME)
  }
}

/**
 * 获取当前实际显示的主题（不是用户偏好）
 * @returns {string} 'light' | 'dark'
 */
export function getActualTheme () {
  return document.documentElement.getAttribute('data-theme') === DARK_THEME
    ? DARK_THEME
    : LIGHT_THEME
}

/**
 * 判断当前是否为深色模式
 * @returns {boolean}
 */
export function isDarkMode () {
  return getActualTheme() === DARK_THEME
}



// WEBPACK FOOTER //
// ./src/utils/themeUtils.js